/**
 * Öffentlicher Verkehr: Haltestelle finden, Abfahrten holen.
 *
 * Quelle ist transport.opendata.ch, dieselbe wie bei der Verbindungssuche.
 * Ohne Schlüssel, mit freigegebenem CORS, rund 0,4 Sekunden je Abfrage - und
 * mit dem, worauf es an einer Haltestelle ankommt: Linie, Ziel, Zeit,
 * Verspätung, Gleis.
 *
 * Der Dienst wird ehrenamtlich betrieben und ist auf 1000 Abfragen pro Tag
 * begrenzt. Deshalb wird nur gefragt, wenn jemand eine Haltestelle antippt -
 * nicht im Hintergrund, nicht auf Vorrat.
 */
const BASIS = 'https://transport.opendata.ch/v1';

/** POI-Klassen, die eine Abfahrtstafel verdienen. */
export const HALT_KLASSEN = new Set([
  'bus', 'railway', 'rail', 'tram', 'subway', 'ferry_terminal', 'aerialway'
]);

const cache = new Map();

async function hole(pfad, signal) {
  const res = await fetch(`${BASIS}/${pfad}`, { signal });
  if (!res.ok) throw new Error(`Fahrplan ${res.status}`);
  return res.json();
}

/**
 * Nächstgelegene Haltestelle zu einem Punkt. Die Suche über Koordinaten ist
 * verlässlicher als über den Namen: "Bahnhof" gibt es hundertfach, und OSM
 * schreibt Haltestellen anders als der Fahrplan.
 */
export async function haltestelleBei({ lat, lng }, { signal } = {}) {
  const key = `h:${lat.toFixed(4)},${lng.toFixed(4)}`;
  if (cache.has(key)) return cache.get(key);
  const p = (async () => {
    // Achtung: der Dienst nennt die Breite `x` und die Laenge `y` - dieselbe
    // Vertauschung wie bei den Halten einer Fahrt weiter unten. Mit `x=lng`
    // landete die Abfrage fuer Luzern bei Ancona, 5096 km entfernt, und die
    // Tafel blieb leer.
    const d = await hole(`locations?x=${lat}&y=${lng}`, signal);
    // Ohne `id` ist es eine Adresse, keine Haltestelle.
    const treffer = (d.stations || []).filter((s) => s.id);
    if (!treffer.length) return null;
    const s = treffer[0];
    // Riegel gegen vertauschte Koordinaten: der Dienst antwortet auch auf
    // Unsinn bereitwillig. Mit x und y verwechselt kam fuer Luzern einmal
    // Ancona zurueck, 5096 km entfernt - und die Tafel blieb stumm, statt
    // den Fehler zu zeigen. Was weiter als 5 km weg ist, kann nicht die
    // Haltestelle sein, die jemand angetippt hat.
    if (Number.isFinite(s.distance) && s.distance > 5000) {
      console.warn(`Fahrplan: naechste Haltestelle "${s.name}" liegt ${Math.round(s.distance / 1000)} km entfernt - verworfen.`);
      return null;
    }
    return { id: s.id, name: s.name, distanz: s.distance ?? null };
  })().catch(() => null);
  cache.set(key, p);
  return p;
}

/** Nächste Abfahrten. Bewusst nicht zwischengespeichert - sie ändern sich. */
export async function abfahrten(stationId, { limit = 8, signal } = {}) {
  const d = await hole(`stationboard?id=${encodeURIComponent(stationId)}&limit=${limit}`, signal);
  return (d.stationboard || []).map((j) => {
    const s = j.stop || {};
    const zeit = s.departureTimestamp ? new Date(s.departureTimestamp * 1000) : null;
    return {
      linie: `${j.category || ''}${j.number ? ' ' + j.number : ''}`.trim() || j.name || '',
      kuerzel: `${j.category || ''}${j.number || ''}`.trim(),
      // Getrennt, weil das Linienzeichen beides einzeln braucht.
      kategorie: j.category || '',
      nummer: j.number ? String(j.number) : '',
      ziel: j.to || '',
      zeit,
      verspaetung: Number.isFinite(s.delay) ? s.delay : null,
      gleis: s.platform || '',
      // Der Fahrplan liefert die weiteren Halte gleich mit - das spart eine
      // zweite Abfrage, wenn jemand die Fahrt öffnet.
      halte: (j.passList || [])
        .map((h, i) => {
          // Der erste Eintrag ist die Haltestelle, deren Tafel wir ansehen -
          // und der Dienst schickt sie ohne Namen und ohne Koordinaten.
          // Beides steht aber in `stop.station` der Fahrt selbst. Ohne diesen
          // Ersatz faellt der Starthalt aus der Liste, und die Fahrt sieht
          // aus, als begaenne sie erst am zweiten Halt.
          const station = h.station?.name ? h.station : (i === 0 ? (j.stop?.station || {}) : {});
          const k = station.coordinate || {};
          return {
            name: station.name || '',
            // Achtung: der Dienst nennt die Breite `x` und die Länge `y`.
            lat: Number.isFinite(k.x) ? k.x : null,
            lng: Number.isFinite(k.y) ? k.y : null,
            zeit: h.departureTimestamp ? new Date(h.departureTimestamp * 1000)
              : (h.arrivalTimestamp ? new Date(h.arrivalTimestamp * 1000) : null),
            verspaetung: Number.isFinite(h.delay) ? h.delay : null
          };
        })
        .filter((h) => h.name)
    };
  });
}

/** Minuten bis zur Abfahrt - "jetzt", "4 min", sonst die Uhrzeit. */
export function inKuerze(zeit) {
  if (!zeit) return '';
  const min = Math.round((zeit.getTime() - Date.now()) / 60000);
  // Was gerade abfaehrt, steht als "jetzt" - "abgefahren" waere zwar genauer,
  // aber an der Haltestelle nutzlos. Aelteres filtert die Tafel ohnehin weg.
  if (min <= 0) return 'jetzt';
  if (min <= 30) return `${min} min`;
  return `${String(zeit.getHours()).padStart(2, '0')}:${String(zeit.getMinutes()).padStart(2, '0')}`;
}

/* --------------------------------------------- Streckenverlauf aus OSM */

const OVERPASS = [
  'https://overpass.osm.ch/api/interpreter',
  'https://overpass-api.de/api/interpreter'
];

async function overpass(query, signal) {
  let fehler = null;
  for (const url of OVERPASS) {
    try {
      const r = await fetch(url, { method: 'POST', signal, body: query });
      if (!r.ok) throw new Error(`Overpass ${r.status}`);
      return (await r.json()).elements || [];
    } catch (e) {
      if (e.name === 'AbortError') throw e;
      fehler = e;
    }
  }
  throw fehler;
}

/**
 * Fahrplan-Kategorie -> `route`-Wert der OSM-Linienrelationen.
 *
 * Ohne diese Einschränkung liefert die Abfrage an einem Bahnhof alles, was
 * dort vorbeikommt - Bus, Tram und Zug durcheinander.
 */
const BAHN = 'train|light_rail';
const BUS = 'bus|trolleybus';
const TYPEN = {
  IC: BAHN, ICE: BAHN, EC: BAHN, TGV: BAHN, RJX: BAHN, NJ: BAHN, EN: BAHN,
  IR: BAHN, IRE: BAHN, RE: BAHN, R: BAHN, S: BAHN, SN: BAHN, PE: BAHN, EXT: BAHN,
  M: 'subway', T: 'tram|light_rail', NFT: 'tram|light_rail',
  B: BUS, BUS: BUS, NFB: BUS, NFO: BUS, KB: BUS, EXB: BUS,
  BAT: 'ferry', FUN: 'funicular', ASC: 'funicular',
  GB: 'aerialway', LB: 'aerialway', PB: 'aerialway', CC: 'aerialway'
};
const ALLE_TYPEN = `${BAHN}|${BUS}|subway|tram|ferry|funicular|aerialway`;

/* ------------------------------------------------------------- Geometrie */

const RAD = Math.PI / 180;
const ERDE = 6371000;

/** Abstand in Metern, flach gerechnet - auf diesen Längen genau genug. */
function abstand(a, b) {
  const dx = (b[0] - a[0]) * Math.cos(((a[1] + b[1]) / 2) * RAD);
  const dy = b[1] - a[1];
  return ERDE * RAD * Math.hypot(dx, dy);
}

/** Punkt auf einer Linie, der einem Ort am nächsten liegt. */
function naechster(linie, punkt) {
  let index = 0;
  let nah = Infinity;
  for (let i = 0; i < linie.length; i++) {
    const d = abstand(linie[i], punkt);
    if (d < nah) { nah = d; index = i; }
  }
  return { index, abstand: nah };
}

function laengeVon(linie) {
  let l = 0;
  for (let i = 1; i < linie.length; i++) l += abstand(linie[i - 1], linie[i]);
  return l;
}

/**
 * Teilstücke zu durchgehenden Linien verketten.
 *
 * Die Wege einer Relation stehen meist in der Reihenfolge der Fahrt, aber
 * nicht zwingend in der richtigen Richtung - OSM-Wege sind so digitalisiert,
 * wie es gerade passte. Deshalb wird jedes Stück dort angehängt, wo sein Ende
 * liegt, vorne oder hinten. Passt keines mehr (fehlender Weg in der
 * Relation), beginnt eine neue Kette: ein Sprung quer durchs Gelände wäre
 * genau die gerade Linie, die hier nicht hingehört.
 */
const NAHT = 25;        // Meter, die zwei Teilstücke auseinanderliegen dürfen

function verketten(wege) {
  const offen = wege
    .map((w) => w.geometry.map((g) => [g.lon, g.lat]))
    .filter((p) => p.length > 1);
  if (!offen.length) return [];

  const ketten = [];
  let kette = offen.shift();
  while (offen.length) {
    const anfang = kette[0];
    const ende = kette[kette.length - 1];
    let beste = -1;
    let wie = null;
    let nah = Infinity;
    offen.forEach((p, i) => {
      const kandidaten = [
        [abstand(ende, p[0]), 'hinten'],
        [abstand(ende, p[p.length - 1]), 'hinten-gedreht'],
        [abstand(anfang, p[0]), 'vorne-gedreht'],
        [abstand(anfang, p[p.length - 1]), 'vorne']
      ];
      for (const [d, k] of kandidaten) if (d < nah) { nah = d; beste = i; wie = k; }
    });
    if (nah > NAHT) { ketten.push(kette); kette = offen.shift(); continue; }
    const p = offen.splice(beste, 1)[0];
    if (wie === 'hinten') kette.push(...p.slice(1));
    else if (wie === 'hinten-gedreht') kette.push(...p.slice(0, -1).reverse());
    else if (wie === 'vorne-gedreht') kette.unshift(...p.slice(1).reverse());
    else kette.unshift(...p.slice(0, -1));
  }
  ketten.push(kette);
  return ketten;
}

/**
 * Aus der ganzen Linie das Stück zwischen erstem und letztem Halt.
 *
 * Eine Relation beschreibt den Weg von Endstation zu Endstation; gefahren
 * wird davon nur ein Abschnitt. Welcher, sagen die Halte: ihre nächsten
 * Punkte auf der Linie begrenzen ihn, ihre Reihenfolge gibt die Richtung.
 */
function ausschneiden(kette, halte) {
  const index = halte.map((h) => naechster(kette, h).index);
  const von = Math.min(...index);
  const bis = Math.max(...index);
  if (bis - von < 1) return null;
  const stueck = kette.slice(von, bis + 1);
  // Rückwärts gefahren: der erste Halt liegt hinter dem letzten.
  return index[0] > index[index.length - 1] ? stueck.reverse() : stueck;
}

/* ---------------------------------------------------------- Die Abfrage */

/**
 * Höchstens so viele Halte stehen in der Abfrage. Jeder Halt ist ein
 * zusätzlicher Filter; fünfzehn genügen, um eine Linie eindeutig zu
 * bestimmen, und halten die Abfrage kurz.
 */
const MAX_FILTER = 15;

function ausgewaehlt(punkte) {
  if (punkte.length <= MAX_FILTER) return punkte;
  const schritt = (punkte.length - 1) / (MAX_FILTER - 1);
  return Array.from({ length: MAX_FILTER }, (_, i) => punkte[Math.round(i * schritt)]);
}

/**
 * Relationen, die an allen übergebenen Halten vorbeikommen.
 *
 * Das Kürzel aus dem Fahrplan taugt nicht zum Suchen: bei Fernverkehrszügen
 * steht dort die Zugnummer ("IR 002562"), in OSM aber die Linie ("IR70").
 * Die Geografie ist eindeutig - jeder Halt filtert die Menge weiter, bis nur
 * noch Linien übrig sind, die wirklich dort fahren.
 */
function abfrage(punkte, typ, radius, ausgabe) {
  const um = (p) => `${p[1].toFixed(5)},${p[0].toFixed(5)}`;
  const zeilen = [
    '[out:json][timeout:60];',
    `relation(around:${radius},${um(punkte[0])})[type=route][route~"^(${typ})$"]->.r0;`
  ];
  punkte.slice(1).forEach((p, i) => {
    zeilen.push(`relation.r${i}(around:${radius},${um(p)})->.r${i + 1};`);
  });
  zeilen.push(`.r${punkte.length - 1} out ${ausgabe};`);
  return zeilen.join('\n');
}

const refSchluessel = (s) => String(s || '').toUpperCase().replace(/[^A-Z0-9]/g, '');

/**
 * Welche Relation zuerst mit Geometrie geholt wird.
 *
 * Geometrie ist teuer - eine Bahnlinie bringt leicht ein halbes Megabyte.
 * Deshalb kommen erst nur die Merkmale, und nur die aussichtsreichsten
 * Relationen werden ganz geladen: passendes Kürzel und passendes Ziel.
 */
function vorsortieren(relationen, { ref, nummer, ziel }) {
  const kuerzel = new Set([ref, nummer].filter(Boolean).map(refSchluessel));
  const zielWort = String(ziel || '').toLowerCase().split(/[ ,]/)[0];
  return relationen
    .map((r) => {
      const t = r.tags || {};
      let punkte = 0;
      if (kuerzel.size && kuerzel.has(refSchluessel(t.ref))) punkte += 4;
      const text = `${t.name || ''} ${t.to || ''}`.toLowerCase();
      if (zielWort && zielWort.length > 2 && text.includes(zielWort)) punkte += 2;
      if (t.ref) punkte += 1;
      return { id: r.id, punkte };
    })
    .sort((a, b) => b.punkte - a.punkte)
    .map((r) => r.id);
}

/** So viele Relationen werden mit Geometrie geholt. */
const MIT_GEOMETRIE = 4;

/** Weiter als das darf kein Halt vom gefundenen Verlauf entfernt liegen. */
const MAX_ABWEICHUNG = 250;

function besterVerlauf(relationen, halte) {
  let beste = null;
  for (const r of relationen) {
    // Rolle `platform` und `stop` sind Bahnsteige und Haltepunkte, nicht der
    // Fahrweg - mitgezeichnet ergeben sie Striche quer zur Linie.
    const wege = (r.members || []).filter(
      (m) => m.type === 'way' && m.geometry?.length && !/platform|stop/.test(m.role || '')
    );
    for (const kette of verketten(wege)) {
      if (kette.length < 2) continue;
      const stueck = ausschneiden(kette, halte);
      if (!stueck) continue;
      const abweichung = halte.reduce((s, h) => s + naechster(stueck, h).abstand, 0) / halte.length;
      if (abweichung > MAX_ABWEICHUNG) continue;
      // Gleich genau, aber kürzer ist besser: so gewinnt bei Ringlinien der
      // kurze Weg, nicht die Runde herum.
      const rang = Math.round(abweichung / 25) * 1e9 + laengeVon(stueck);
      if (!beste || rang < beste.rang) {
        beste = { rang, abweichung, coordinates: stueck, name: r.tags?.name || '', ref: r.tags?.ref || '' };
      }
    }
  }
  return beste;
}

const verlaufCache = new Map();

/**
 * Tatsächlicher Verlauf einer Fahrt zwischen ihren Halten - Gleis für Gleis,
 * Strasse für Strasse, aus den OSM-Linienrelationen.
 *
 * Gibt es nichts Passendes, kommt `null` zurück. Dann wird nichts gezeichnet:
 * eine Luftlinie zwischen den Halten sieht aus wie ein Streckenverlauf, ist
 * aber keiner.
 *
 * Gemessen: Tram 3 Zürich 0,8 s, Bus 21 Bern-Interlaken (41 Halte) 1,0 s,
 * IR70 Luzern-Zürich 2,0 s.
 */
export async function fahrtVerlauf({ halte, kategorie, ref, nummer, ziel }, { signal } = {}) {
  const punkte = [];
  for (const h of halte || []) {
    if (!Number.isFinite(h.lat) || !Number.isFinite(h.lng)) continue;
    const p = [h.lng, h.lat];
    // Derselbe Halt zweimal (Fahrplan wiederholt ihn an Abschnittsgrenzen)
    // bringt nur einen weiteren Filter ohne Erkenntnis.
    if (punkte.length && abstand(punkte[punkte.length - 1], p) < 20) continue;
    punkte.push(p);
  }
  if (punkte.length < 2) return null;

  const typ = TYPEN[String(kategorie || '').toUpperCase()] || ALLE_TYPEN;
  const schluessel = [typ, refSchluessel(ref), refSchluessel(nummer),
    ...punkte.map((p) => `${p[0].toFixed(4)},${p[1].toFixed(4)}`)].join('|');
  if (verlaufCache.has(schluessel)) return verlaufCache.get(schluessel);

  const p = (async () => {
    // Drei Versuche, von genau nach grosszügig: alle Halte auf 300 m, alle auf
    // 700 m (grosse Bahnhöfe liegen weit vom Gleis), zuletzt nur Anfang und
    // Ende - dann entscheidet die Abweichung, ob es die richtige Linie ist.
    const versuche = [
      [ausgewaehlt(punkte), 300],
      [ausgewaehlt(punkte), 700],
      [[punkte[0], punkte[punkte.length - 1]], 700]
    ];
    for (const [filter, radius] of versuche) {
      let merkmale;
      try {
        merkmale = await overpass(abfrage(filter, typ, radius, 'ids tags'), signal);
      } catch (e) {
        if (e.name === 'AbortError') throw e;
        return null;
      }
      const ids = vorsortieren(merkmale.filter((e) => e.type === 'relation'), { ref, nummer, ziel })
        .slice(0, MIT_GEOMETRIE);
      if (!ids.length) continue;
      let voll;
      try {
        voll = await overpass(`[out:json][timeout:60];relation(id:${ids.join(',')});out geom;`, signal);
      } catch (e) {
        if (e.name === 'AbortError') throw e;
        return null;
      }
      const beste = besterVerlauf(voll.filter((e) => e.type === 'relation'), punkte);
      if (beste) return beste;
    }
    return null;
  })();

  verlaufCache.set(schluessel, p);
  // Ein Fehlschlag soll sich nicht einbrennen - beim nächsten Mal neu fragen.
  p.then((v) => { if (!v) verlaufCache.delete(schluessel); }).catch(() => verlaufCache.delete(schluessel));
  return p;
}

/* --------------------------------------------- Wo die Fahrt gerade ist */

/**
 * Punkt auf einer Linie nach zurückgelegtem Anteil zwischen zwei Halten.
 *
 * Nicht Luftlinie: der Anteil wird auf dem tatsächlichen Verlauf abgetragen,
 * damit der Zug der Kurve folgt und nicht quer durch den Berg fährt.
 */
function entlangLinie(linie, von, nach, anteil) {
  const a = naechster(linie, [von.lng, von.lat]).index;
  const b = naechster(linie, [nach.lng, nach.lat]).index;
  const [i0, i1] = a <= b ? [a, b] : [b, a];
  const stueck = linie.slice(i0, i1 + 1);
  if (stueck.length < 2) return null;
  if (a > b) stueck.reverse();

  const gesamt = laengeVon(stueck);
  if (!(gesamt > 0)) return null;
  let ziel = gesamt * anteil;
  for (let i = 1; i < stueck.length; i++) {
    const d = abstand(stueck[i - 1], stueck[i]);
    if (ziel <= d || i === stueck.length - 1) {
      const t = d > 0 ? Math.min(1, ziel / d) : 0;
      return [
        stueck[i - 1][0] + (stueck[i][0] - stueck[i - 1][0]) * t,
        stueck[i - 1][1] + (stueck[i][1] - stueck[i - 1][1]) * t
      ];
    }
    ziel -= d;
  }
  return stueck[stueck.length - 1];
}

/**
 * Wo eine Fahrt gerade sein dürfte.
 *
 * Das ist **keine Ortung**. Der offene Fahrplan kennt keine Fahrzeugposition;
 * er kennt Zeiten und Verspätungen. Daraus lässt sich ausrechnen, welchen
 * Abschnitt die Fahrt gerade zurücklegt und wie weit sie darin ist - und
 * weil wir den echten Linienverlauf haben, lässt sich dieser Anteil auf dem
 * Gleis abtragen statt auf einer Geraden. Für die Anzeige ist das nah genug;
 * sicher ist daran nur, was der Fahrplan sagt.
 */
export function fahrtPosition({ halte, coordinates }, jetzt = Date.now()) {
  const punkte = (halte || []).filter(
    (h) => h.zeit && Number.isFinite(h.lat) && Number.isFinite(h.lng)
  );
  if (punkte.length < 2) return null;

  // Die Verspätung gilt am jeweiligen Halt - sie verschiebt dessen Zeit.
  const zeitpunkt = (h) => h.zeit.getTime() + (h.verspaetung || 0) * 60000;

  const erster = punkte[0];
  const letzter = punkte[punkte.length - 1];
  if (jetzt <= zeitpunkt(erster)) {
    return {
      status: 'vor',
      lngLat: [erster.lng, erster.lat],
      nach: erster,
      inSekunden: Math.round((zeitpunkt(erster) - jetzt) / 1000),
      anteil: 0
    };
  }
  if (jetzt >= zeitpunkt(letzter)) {
    return { status: 'angekommen', lngLat: [letzter.lng, letzter.lat], nach: letzter, anteil: 1 };
  }

  let i = 0;
  while (i < punkte.length - 2 && jetzt > zeitpunkt(punkte[i + 1])) i++;
  const von = punkte[i];
  const nach = punkte[i + 1];
  const spanne = zeitpunkt(nach) - zeitpunkt(von);
  const anteil = spanne > 0 ? Math.min(1, Math.max(0, (jetzt - zeitpunkt(von)) / spanne)) : 0;

  // Ohne Verlauf bleibt die Gerade zwischen zwei benachbarten Halten - das
  // sind selten mehr als ein paar Kilometer, und es ist ein Punkt, keine
  // Linie, die etwas Falsches behaupten würde.
  const auf = coordinates?.length > 1
    ? entlangLinie(coordinates, von, nach, anteil)
    : null;

  return {
    status: 'unterwegs',
    lngLat: auf || [
      von.lng + (nach.lng - von.lng) * anteil,
      von.lat + (nach.lat - von.lat) * anteil
    ],
    von,
    nach,
    anteil,
    inSekunden: Math.round((zeitpunkt(nach) - jetzt) / 1000)
  };
}
